import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { conferenceRoomApi, amenityApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';

function ConferenceRoomDetails() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [formData, setFormData] = useState({
    roomName: '',
    roomType: '',
    tower: '',
    floor: '',
    capacity: '',
    amenities: [],
    status: 'ACTIVE'
  });

  useEffect(() => {
    if (roomId) {
      fetchRoomDetails();
      loadAmenities();
    }
  }, [roomId]);

  const loadAmenities = async () => {
    try {
      const data = await amenityApi.getActive();
      const amenitiesList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setAvailableAmenities(amenitiesList);
    } catch (error) {
      console.error('Failed to load amenities:', error);
    }
  };

  const fetchRoomDetails = async () => {
    try {
      setIsLoading(true);
      const response = await conferenceRoomApi.getById(roomId);
      const roomData = response?.data || response;

      if (roomData) {
        setRoom(roomData);
        // Parse amenities string into array
        const amenitiesArray = roomData.amenities ? roomData.amenities.split(',').map(a => a.trim()) : [];
        setFormData({
          roomName: roomData.roomName || '',
          roomType: roomData.roomType || '',
          tower: roomData.tower || '',
          floor: roomData.floor || '',
          capacity: roomData.capacity || '',
          amenities: amenitiesArray,
          status: roomData.status || 'ACTIVE'
        });
      } else {
        setError('Conference room not found');
      }
    } catch (err) {
      console.error('Error fetching room details:', err);
      setError('Failed to load conference room details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/community/conference-rooms');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    const amenitiesArray = room.amenities ? room.amenities.split(',').map(a => a.trim()) : [];
    setFormData({
      roomName: room.roomName || '',
      roomType: room.roomType || '',
      tower: room.tower || '',
      floor: room.floor || '',
      capacity: room.capacity || '',
      amenities: amenitiesArray,
      status: room.status || 'ACTIVE'
    });
  };

  const handleSave = async () => {
    try {
      await conferenceRoomApi.update(roomId, {
        roomName: formData.roomName,
        roomType: formData.roomType,
        tower: formData.tower,
        floor: formData.floor,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        amenities: formData.amenities.length > 0 ? formData.amenities.join(',') : null,
      });

      toast.success('Conference room updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });

      setIsEditing(false);
      await fetchRoomDetails();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleAmenityToggle = (amenityName) => {
    setFormData(prev => {
      const amenitiesArray = Array.isArray(prev.amenities) ? prev.amenities : [];
      const newAmenities = amenitiesArray.includes(amenityName)
        ? amenitiesArray.filter(a => a !== amenityName)
        : [...amenitiesArray, amenityName];
      return { ...prev, amenities: newAmenities };
    });
  };

  const backButton = (
    <button
      className="admin-btn admin-btn-secondary"
      onClick={handleBack}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Back to Conference Rooms
    </button>
  );

  if (isLoading) {
    return (
      <div className="admin-view conference-room-details-view">
        <PageHeader
          breadcrumbs={["Community", "Conference Rooms", "Room Details"]}
          actionButton={backButton}
        />
        <div className="user-details-loading">
          <div className="loading-skeleton">
            <div className="skeleton-header"></div>
            <div className="skeleton-content">
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-view conference-room-details-view">
        <PageHeader
          breadcrumbs={["Community", "Conference Rooms", "Room Details"]}
          actionButton={backButton}
        />
        <div className="error-state">
          <div className="error-message">{error}</div>
          <button
            className="admin-btn admin-btn-primary"
            onClick={fetchRoomDetails}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view conference-room-details-view">
      <PageHeader
        breadcrumbs={["Community", "Conference Rooms", "Room Details"]}
        actionButton={backButton}
      />

      <div className="user-details-container">
        {/* Room Information Card */}
        <div className="user-info-card card">
          <div className="user-header">
            <div className="user-basic-info">
              <h2 className="user-name">{room?.roomName || 'N/A'}</h2>
              <p className="user-role">{room?.roomType || '-'}</p>
              <div className="user-status">
                <span className={`status-badge ${room?.status?.toLowerCase() || 'active'}`}>
                  {room?.status || 'ACTIVE'}
                </span>
              </div>
            </div>
            {!isEditing && (
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleEdit}
              >
                Edit Room
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="user-details-grid" style={{ marginTop: '24px' }}>
              <div className="detail-item">
                <label htmlFor="roomName">Room Name</label>
                <input
                  type="text"
                  id="roomName"
                  name="roomName"
                  value={formData.roomName}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>
              <div className="detail-item">
                <label htmlFor="roomType">Room Type</label>
                <select
                  id="roomType"
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleInputChange}
                  className="input select"
                  required
                >
                  <option value="">Select room type</option>
                  <option value="Meeting Room">Meeting Room</option>
                  <option value="Conference Room">Conference Room</option>
                  <option value="Medical Room">Medical Room</option>
                  <option value ="Rest Room">Rest Room</option>
                  <option value="Other">Other</option>

                </select>
              </div>
              <div className="detail-item">
                <label htmlFor="tower">Tower</label>
                <input
                  type="text"
                  id="tower"
                  name="tower"
                  value={formData.tower}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>
              <div className="detail-item">
                <label htmlFor="floor">Floor</label>
                <input
                  type="text"
                  id="floor"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>
              <div className="detail-item">
                <label htmlFor="capacity">Capacity</label>
                <input
                  type="number"
                  id="capacity"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  className="input"
                  min="1"
                  placeholder="Room capacity"
                />
              </div>
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <label>Amenities</label>
                {availableAmenities.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#666' }}>Loading amenities...</p>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '10px',
                    marginTop: '10px'
                  }}>
                    {availableAmenities.map(amenity => (
                      <label key={amenity.id || amenity.name} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        border: formData.amenities.includes(amenity.name) ? '2px solid #4CAF50' : '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: formData.amenities.includes(amenity.name) ? '#e8f5e9' : 'white',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity.name)}
                          onChange={() => handleAmenityToggle(amenity.name)}
                          style={{ marginRight: '10px' }}
                        />
                        <span style={{ fontSize: '20px', marginRight: '8px' }}>{amenity.icon || '✓'}</span>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>{amenity.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="detail-item">
                <label>Location</label>
                <span>{room?.location || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Created By</label>
                <span>{room?.createdBy || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Created Date</label>
                <span>{room?.createdDate ? new Date(room.createdDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span>
              </div>
            </div>
          ) : (
            <div className="user-details-grid">
              <div className="detail-item">
                <label>Room Name</label>
                <span>{room?.roomName || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Room Type</label>
                <span>{room?.roomType || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Tower</label>
                <span>{room?.tower || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Floor</label>
                <span>{room?.floor || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Capacity</label>
                <span>{room?.capacity || '-'}</span>
              </div>
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <label>Amenities</label>
                {room?.amenities ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {room.amenities.split(',').map((amenity, idx) => {
                      const trimmedAmenity = amenity.trim();
                      const matchedAmenity = availableAmenities.find(a => a.name === trimmedAmenity);
                      return (
                        <span key={idx} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 12px',
                          backgroundColor: '#e8f5e9',
                          border: '1px solid #4CAF50',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 500
                        }}>
                          <span style={{ fontSize: '18px', marginRight: '6px' }}>
                            {matchedAmenity?.icon || '✓'}
                          </span>
                          {trimmedAmenity}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <span>No amenities specified</span>
                )}
              </div>
              <div className="detail-item">
                <label>Location</label>
                <span>{room?.location || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Created By</label>
                <span>{room?.createdBy || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Created Date</label>
                <span>{room?.createdDate ? new Date(room.createdDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span>
              </div>
            </div>
          )}

          {isEditing && (
            <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConferenceRoomDetails;
